import { useRoute } from 'vue-router'

import type { TabKey } from '@/components/base/BaseTab.vue'

export type WebappPath = 'WelcomePage' | 'HomePage' | 'CompleteProfilePreview'

export type AppearanceTab = 'home' | 'phone' | 'loyalty' | 'storeSelection' | 'profileInfo' | 'optin'

export const pathForZone = (zone: AppearanceTab): WebappPath => {
  if (zone === 'storeSelection' || zone === 'profileInfo') return 'CompleteProfilePreview'
  if (zone === 'loyalty' || zone === 'optin') return 'HomePage'

  return 'WelcomePage'
}

export const defaultZoneForTab = (tab: string): AppearanceTab => {
  if (tab === 'missions') return 'loyalty'
  if (tab === 'config') return 'storeSelection'

  return 'home'
}

export default function useWebappTabs(): {
  currentAppearanceTab: Ref<AppearanceTab>
  tabs: Ref<TabKey[]>
  currentTab: Ref<string>
  shareTabs: Ref<TabKey[]>
  currentShareTab: Ref<string>
} {
  const { t } = useI18n()
  const route = useRoute()

  const currentTab = ref((route?.query?.activeTab as string) || 'appearance')
  const currentAppearanceTab = ref<AppearanceTab>(defaultZoneForTab(currentTab.value))
  const currentShareTab = ref<'link' | 'qrCode' | 'flyer'>(
    (route?.query?.activeShareTab as 'link' | 'qrCode' | 'flyer') || 'link'
  )

  watch(currentTab, (tab) => {
    currentAppearanceTab.value = defaultZoneForTab(tab)
  })

  const tabs = ref<TabKey[]>([
    {
      value: 'appearance',
      name: t('pages.loyaltySupports.webapp.tabs.appearance'),
      icon: 'HeroPaintBrushIconOutline'
    },
    {
      value: 'texts',
      name: t('pages.loyaltySupports.webapp.tabs.texts'),
      icon: 'HeroClipboardDocumentListIconOutline'
    },
    {
      value: 'config',
      name: t('pages.loyaltySupports.webapp.tabs.config'),
      icon: 'HeroCog8ToothIconOutline'
    },
    {
      value: 'missions',
      name: t('pages.loyaltySupports.webapp.tabs.missions'),
      icon: 'HeroFlagIconOutline'
    }
  ])

  const shareTabs = ref<TabKey[]>([
    {
      value: 'link',
      name: t('pages.loyaltySupports.webapp.appearance.shareTab'),
      icon: 'HeroLinkIconOutline'
    },
    {
      value: 'qrCode',
      name: t('pages.loyaltySupports.webapp.appearance.qrCodeTab'),
      icon: 'HeroQrCodeIconOutline'
    },
    {
      value: 'flyer',
      name: t('pages.loyaltySupports.webapp.appearance.flyerTab'),
      icon: 'HeroDocumentIconOutline'
    }
  ])

  return {
    currentAppearanceTab,
    currentShareTab,
    currentTab,
    shareTabs,
    tabs
  }
}
